// FE-PINV-071 / 073 — List view URL state.
//
// Encodes the List view's per-surface state in the page URL:
//
//   ?sort=<field>&dir=asc|desc           click-to-sort (FE-PINV-071)
//   ?density=comfortable|compact         row density   (FE-PINV-071, canonical
//                                                       control in FE-PINV-073)
//   ?cols=<csv>                          visible column ids (FE-PINV-073)
//   ?colsOrder=<csv>                     full canonical-order list
//                                          (FE-PINV-073; visible is a subset)
//   ?visible=<n>                         "Load more" pagination window
//                                          (FE-PINV-071)
//
// The shape is intentionally orthogonal to the Pipeline-tab's filter
// state (FE-PINV-064 / filterState.ts) — those params (cadence/stage/
// lives_band/...) round-trip through the toolbar and are shared with
// Grid + Kanban. The list-view-only knobs live here.

import type { Density } from "./filterState";


export type ListColumnId =
  | "select"
  | "prospect"
  | "opportunity"
  | "stage"
  | "type"
  | "lives"
  | "renewal"
  | "producer"
  | "est_arr"
  | "last_activity"
  | "next_step"
  | "alerts";


export type ListSortField =
  | "prospect"
  | "opportunity"
  | "stage"
  | "type"
  | "lives"
  | "renewal"
  | "producer"
  | "est_arr"
  | "last_activity"
  | "next_step"
  | "alerts";


export type SortDir = "asc" | "desc";


export interface ListSort {
  field: ListSortField | null;
  dir: SortDir;
}


export interface ListUrlState {
  sort: ListSort;
  density: Density;
  visible: ListColumnId[];
  order: ListColumnId[];
  /** Pagination window. Number of rows currently rendered. */
  loadedRows: number;
}


/** Canonical column order — the source of truth for "Reset to default"
 *  in FE-PINV-073 and the initial render of FE-PINV-071. The leading
 *  ``select`` column is the multi-select checkbox; it's never hidable
 *  (FE-PINV-072 owns the bulk-select surface). */
export const DEFAULT_COLUMN_ORDER: ListColumnId[] = [
  "select", "prospect", "opportunity", "stage", "type", "lives",
  "renewal", "producer", "est_arr", "last_activity", "next_step",
  "alerts",
];


export const DEFAULT_VISIBLE_COLUMNS: ListColumnId[] = [
  ...DEFAULT_COLUMN_ORDER,
];


export const DEFAULT_DENSITY: Density = "comfortable";


/** Initial pagination window before any "Load more" click. */
export const DEFAULT_LOADED_ROWS = 50;


/** Hard cap on the rendered window. At this number the footer swaps
 *  the "Load more" button for "Switch to refined filters" copy
 *  (FE-PINV-071 perf safety rail). */
export const MAX_LOADED_ROWS = 500;


const _ALL_COLUMN_IDS = new Set<ListColumnId>(DEFAULT_COLUMN_ORDER);


const _SORT_FIELDS = new Set<ListSortField>([
  "prospect", "opportunity", "stage", "type", "lives", "renewal",
  "producer", "est_arr", "last_activity", "next_step", "alerts",
]);


function _readCsvColumns(raw: string | null): ListColumnId[] | null {
  if (!raw) return null;
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  const filtered = parts.filter((p): p is ListColumnId =>
    _ALL_COLUMN_IDS.has(p as ListColumnId),
  );
  // Drop the param entirely when nothing valid survives; surface the
  // canonical defaults rather than an empty table.
  return filtered.length > 0 ? filtered : null;
}


export function readListUrlState(params: URLSearchParams): ListUrlState {
  // Sort.
  const sortRaw = params.get("sort");
  const dirRaw = params.get("dir");
  const sortField: ListSortField | null =
    sortRaw && _SORT_FIELDS.has(sortRaw as ListSortField)
      ? (sortRaw as ListSortField)
      : null;
  const dir: SortDir = dirRaw === "desc" ? "desc" : "asc";

  // Density.
  const densityRaw = params.get("density");
  const density: Density =
    densityRaw === "compact" || densityRaw === "comfortable"
      ? densityRaw
      : DEFAULT_DENSITY;

  // Column order — canonical order survives reload; visibility is a
  // subset of it.
  const order = _readCsvColumns(params.get("colsOrder"))
    ?? [...DEFAULT_COLUMN_ORDER];
  // ``select`` is force-pinned to position 0 — bulk-select is a fixed
  // affordance per spec.
  const orderNormalized = ["select" as ListColumnId]
    .concat(order.filter((c) => c !== "select"));

  const visibleParsed = _readCsvColumns(params.get("cols"));
  const visible = visibleParsed
    ? ["select" as ListColumnId].concat(
        visibleParsed.filter((c) => c !== "select"),
      )
    : [...DEFAULT_VISIBLE_COLUMNS];

  // Loaded rows.
  const visibleRaw = params.get("visible");
  let loadedRows = DEFAULT_LOADED_ROWS;
  if (visibleRaw) {
    const n = parseInt(visibleRaw, 10);
    if (Number.isFinite(n) && n > 0) {
      loadedRows = Math.min(n, MAX_LOADED_ROWS);
    }
  }

  return {
    sort: { field: sortField, dir },
    density,
    visible,
    order: orderNormalized,
    loadedRows,
  };
}


export function writeListUrlState(
  state: ListUrlState,
  baseParams: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(baseParams);

  if (state.sort.field) {
    out.set("sort", state.sort.field);
    out.set("dir", state.sort.dir);
  } else {
    out.delete("sort");
    out.delete("dir");
  }

  if (state.density !== DEFAULT_DENSITY) {
    out.set("density", state.density);
  } else {
    out.delete("density");
  }

  // Only emit ``cols`` when the visible set diverges from the
  // canonical default (keeps URLs short on the default render).
  const visibleNonSelect = state.visible.filter((c) => c !== "select");
  const defaultVisibleNonSelect = DEFAULT_VISIBLE_COLUMNS
    .filter((c) => c !== "select");
  if (
    visibleNonSelect.length !== defaultVisibleNonSelect.length
    || visibleNonSelect.some((c, i) => c !== defaultVisibleNonSelect[i])
  ) {
    out.set("cols", visibleNonSelect.join(","));
  } else {
    out.delete("cols");
  }

  const orderNonSelect = state.order.filter((c) => c !== "select");
  const defaultOrderNonSelect = DEFAULT_COLUMN_ORDER
    .filter((c) => c !== "select");
  if (
    orderNonSelect.length !== defaultOrderNonSelect.length
    || orderNonSelect.some((c, i) => c !== defaultOrderNonSelect[i])
  ) {
    out.set("colsOrder", orderNonSelect.join(","));
  } else {
    out.delete("colsOrder");
  }

  if (state.loadedRows !== DEFAULT_LOADED_ROWS) {
    out.set("visible", String(state.loadedRows));
  } else {
    out.delete("visible");
  }

  return out;
}


/** Click-to-sort transition: default → asc → desc → default. The
 *  caller passes the column the producer clicked. */
export function cycleSort(
  current: ListSort, clicked: ListSortField,
): ListSort {
  if (current.field !== clicked) {
    return { field: clicked, dir: "asc" };
  }
  if (current.dir === "asc") {
    return { field: clicked, dir: "desc" };
  }
  // currently desc on the clicked column — back to default.
  return { field: null, dir: "asc" };
}
